// =============================================================================
// client/src/lib/callSheet.js | v1.0 | SQ-S1 | The Call Sheet — shared client bits
// =============================================================================
// One home for the pieces the board page (pages/CallSheet.jsx) and the profile
// tab (components/CallSheetTab.jsx) both need, so the two cannot drift apart:
// the status badge dict, the UTC-safe date formatter for bare YYYY-MM-DD
// columns (PlanProgress.jsx's formatDateUTC shape), and the overdue test.
//
// "Overdue" is a DISPLAY state, not a stored status: a pending task whose
// deadline_date is before the server's `today` (the API returns today so the
// client and server agree on the calendar date regardless of the viewer's
// timezone).
// =============================================================================

// PLAN_STATUS_COLORS idiom (PlanProgress.jsx:16-21). `overdue` is the one
// display-only key; the other three are the table's CHECK vocabulary.
export const CALL_SHEET_STATUS_COLORS = {
  pending:   'bg-amber-100 text-amber-800',
  overdue:   'bg-red-100 text-red-800',
  completed: 'bg-hunter-deep text-white',
  cancelled: 'bg-slate-200 text-slate-600',
};

export function formatDateUTC(iso, opts = { month: 'short', day: 'numeric', year: 'numeric' }) {
  if (!iso) return '—';
  const d = String(iso).length === 10 ? new Date(`${iso}T00:00:00Z`) : new Date(iso);
  return d.toLocaleDateString('en-US', { ...opts, timeZone: 'UTC' });
}

export function todayLocalISO() {
  // Fallback only, for a default before the API has answered with `today`.
  return new Date().toISOString().slice(0, 10);
}

export function isOverdue(task, today) {
  return task.status === 'pending' && !!today && task.deadline_date < today;
}

/** The badge key to render for a task: 'overdue' beats 'pending'. */
export function displayStatus(task, today) {
  return isOverdue(task, today) ? 'overdue' : task.status;
}

/** Board order: overdue first (oldest deadline first), then pending by deadline. */
export function sortForBoard(tasks, today) {
  return [...tasks].sort((a, b) => {
    const ao = isOverdue(a, today) ? 0 : 1;
    const bo = isOverdue(b, today) ? 0 : 1;
    if (ao !== bo) return ao - bo;
    if (a.deadline_date !== b.deadline_date) return a.deadline_date < b.deadline_date ? -1 : 1;
    return (a.task_seq || 0) - (b.task_seq || 0);
  });
}

/** "x of y" for a quantity-split task; empty for a labeled Core Recruiting component (cw_018). */
export function seqLabel(task) {
  if (task.task_label) return '';
  const total = Number(task.quantity_total) || 1;
  return total > 1 ? `${task.task_seq} of ${total}` : '';
}

/** What a row is called: the component label when exploded, else the service. */
export function taskTitle(task) {
  return task.task_label || task.service_name || '—';
}

/** For labeled component rows, the parent service shown small under the label. */
export function taskParent(task) {
  return task.task_label ? task.service_name || null : null;
}

export function recruitName(task) {
  return [task.recruit_first_name, task.recruit_last_name].filter(Boolean).join(' ') || '—';
}

export function completedByName(task) {
  return [task.completed_by_first_name, task.completed_by_last_name].filter(Boolean).join(' ') || null;
}

/** Server refusals render verbatim (can.js doctrine); anything else gets a plain sentence. */
export function errorMessage(err, fallback = 'Something went wrong') {
  return err?.body?.error || err?.message || fallback;
}
